import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import DfyModal from "@/components/DfyModal";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Check, Clock, Sparkles } from "lucide-react";

export default function Services() {
  const { current, projects, selectProject } = useProject();
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [svc, req] = await Promise.all([
        api.get("/services"),
        api.get("/service-requests"),
      ]);
      setServices(svc.data.services || []);
      setRequests(req.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">
          <Sparkles size={10} className="inline -mt-0.5 mr-1" /> WiredGuru · Done For You
        </div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Services
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Productised offerings from the WiredGuru team. Pick what fits, we
          send a quote within 1 business day. No payment required here —
          WiredGuru handles invoicing separately.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div
            key={s.id}
            data-testid={`service-${s.id}`}
            className="card-flat p-5 flex flex-col"
          >
            <div className="label-eyebrow">{s.turnaround}</div>
            <h3 className="display mt-1 text-lg font-bold tracking-tight">
              {s.name}
            </h3>
            <div className="mt-3 display text-2xl font-black tabular-nums">
              £{s.price_gbp.toLocaleString()}
            </div>
            <p className="mt-3 text-sm text-zinc-600 flex-1">{s.description}</p>
            {s.bullets && (
              <ul className="mt-3 space-y-1.5 text-sm">
                {s.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check
                      size={14}
                      className="mt-[3px] text-emerald-600 shrink-0"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setSelected(s)}
              data-testid={`service-request-${s.id}`}
              className="mt-5 inline-flex items-center justify-center gap-1.5 bg-black text-white text-sm px-4 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors"
            >
              Request
            </button>
          </div>
        ))}
      </div>

      <section className="mt-10 card-flat" data-testid="my-requests">
        <div className="p-5 border-b border-zinc-200">
          <div className="label-eyebrow">Your requests</div>
          <div className="mt-1 text-sm text-zinc-500">
            Every DFY submission is recorded here with its current status.
          </div>
        </div>
        <div className="divide-y divide-zinc-200">
          {loading && (
            <div className="p-6 text-center text-sm text-zinc-500">Loading…</div>
          )}
          {!loading && !requests.length && (
            <div className="p-8 text-center text-sm text-zinc-500">
              No requests yet.
            </div>
          )}
          {requests.map((r) => (
            <div
              key={r.request_id}
              className="p-4 grid grid-cols-12 gap-4 items-center text-sm"
            >
              <div className="col-span-3 mono text-xs text-zinc-500">
                {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="col-span-3 font-medium">
                {r.service_id || r.action_title || "DFY request"}
              </div>
              <div className="col-span-3 text-zinc-600 truncate">
                {r.contact_email}
              </div>
              <div className="col-span-3 text-right">
                <StatusPill status={r.status} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <DfyModal
        open={!!selected}
        service={selected}
        context={{ project_id: current?.project_id }}
        onClose={() => {
          setSelected(null);
          refresh();
        }}
      />
    </DashboardShell>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "closed"
      ? "bg-zinc-100 text-zinc-700 border-zinc-200"
      : status === "contacted"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : status === "notified"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span
      className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${cls}`}
    >
      <Clock size={10} className="mr-1" />
      {status}
    </span>
  );
}
